import { useCallback, useEffect, useRef, useState } from "react";

/** D-16: one line of text plus the column's padding. */
export const ANCHOR_THRESHOLD_PX = 80;

const nextFrame = (cb) => (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function"
	? window.requestAnimationFrame(cb)
	: setTimeout(cb, 16));
const cancelFrame = (id) => (typeof window !== "undefined" && typeof window.cancelAnimationFrame === "function"
	? window.cancelAnimationFrame(id)
	: clearTimeout(id));

/**
 * Follow the bottom only while the reader is already there (chat-experience 11, D-16). Chat.jsx used
 * to scroll to the bottom on every record, every stream chunk and every typing event, which yanked a
 * visitor who had scrolled up to re-read an answer.
 *
 * Only a move UP un-anchors. A smooth scroll we started ourselves passes through positions that are
 * more than the threshold from a bottom that keeps growing under it; reading those as "the reader
 * left" would stop the pane following mid-reply. Content growth never changes `scrollTop`, and our
 * own scrolls only ever increase it, so a decrease is the reader's hand.
 *
 * `ref.current` may be null (the column is conditional in Chat.jsx); every path tolerates it.
 */
export function useScrollAnchor(ref, { threshold = ANCHOR_THRESHOLD_PX } = {}) {
	const [anchored, setAnchored] = useState(true);   // a fresh transcript starts at the bottom
	const [unread, setUnread] = useState(0);
	const anchoredRef = useRef(true);                 // read inside callbacks without re-subscribing
	const lastTopRef = useRef(0);
	const frameRef = useRef(null);
	const scheduledRef = useRef(false);   // a flag, not the frame id: a synchronous rAF returns after its callback ran
	const attachedRef = useRef(null);
	const listenerRef = useRef(null);

	const setAnchor = useCallback((next) => {
		anchoredRef.current = next;
		setAnchored(next);
		if (next) setUnread(0);
	}, []);

	const recompute = useCallback(() => {
		const el = ref.current;
		if (!el) return;
		const top = el.scrollTop;
		const distance = el.scrollHeight - top - el.clientHeight;
		const movedUp = top < lastTopRef.current;
		lastTopRef.current = top;
		if (distance <= threshold) setAnchor(true);
		else if (movedUp) setAnchor(false);
	}, [ref, threshold, setAnchor]);

	// Passive scroll listener, coalesced into ONE recompute per frame (§7 "throttled to a frame").
	const onScroll = useCallback(() => {
		if (scheduledRef.current) return;
		scheduledRef.current = true;
		frameRef.current = nextFrame(() => {
			scheduledRef.current = false;
			frameRef.current = null;
			recompute();
		});
	}, [recompute]);

	const detach = useCallback(() => {
		if (attachedRef.current && listenerRef.current) {
			attachedRef.current.removeEventListener("scroll", listenerRef.current);
		}
		attachedRef.current = null;
		listenerRef.current = null;
		if (scheduledRef.current) cancelFrame(frameRef.current);
		scheduledRef.current = false;
		frameRef.current = null;
	}, []);

	// No dependency list: the container can mount after the first render (or be swapped for the
	// origin alert), so every commit checks whether the element we listen on is still the current one.
	useEffect(() => {
		const el = ref.current;
		if (el === attachedRef.current && listenerRef.current === onScroll) return;
		detach();
		if (!el) return;
		el.addEventListener("scroll", onScroll, { passive: true });
		attachedRef.current = el;
		listenerRef.current = onScroll;
		lastTopRef.current = el.scrollTop;
	});
	useEffect(() => detach, [detach]);

	const scrollToBottom = useCallback((behavior = "auto") => {
		const el = ref.current;
		if (!el || typeof el.scrollTo !== "function") return;
		el.scrollTo({ top: el.scrollHeight, behavior });
	}, [ref]);

	/**
	 * `added` = records appended since the last call (0 while a reply streams into an existing record).
	 * `countUnread: false` for content that is not a message — the typing indicator, a collector, an
	 * inline error — which must neither raise the badge nor move an unanchored reader.
	 */
	const onContentChange = useCallback(({ added = 0, behavior = "auto", countUnread = true } = {}) => {
		if (anchoredRef.current) {
			scrollToBottom(behavior);
			return;
		}
		if (countUnread && added > 0) setUnread((n) => n + added);
	}, [scrollToBottom]);

	/** The visitor's own send always re-anchors (D-16), whatever they were reading. */
	const anchorNow = useCallback((behavior = "smooth") => {
		setAnchor(true);
		scrollToBottom(behavior);
	}, [scrollToBottom, setAnchor]);

	return { anchored, unread, onContentChange, anchorNow, scrollToBottom };
}
